import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";

type Heading = { id: string; text: string };

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function extractHeadings(markdown: string): Heading[] {
  const headings: Heading[] = [];
  for (const line of markdown.split("\n")) {
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) {
      const text = m[1].replace(/[*_`]/g, "").trim();
      headings.push({ id: slugify(text), text });
    }
  }
  return headings;
}

interface MarkdownArticleProps {
  body: string;
}

export function MarkdownArticle({ body }: MarkdownArticleProps) {
  const headings = useMemo(() => extractHeadings(body), [body]);
  const [active, setActive] = useState<string | null>(headings[0]?.id ?? null);

  useEffect(() => {
    if (headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: "-25% 0px -65% 0px", threshold: 0 },
    );
    for (const h of headings) {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [headings]);

  return (
    <div className="grid gap-12 lg:grid-cols-[220px_1fr]">
      {/* TOC */}
      <aside className="hidden lg:block">
        <div className="sticky top-28">
          <p className="mb-4 font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
            On this page
          </p>
          <nav>
            <ul className="space-y-2.5 border-l border-bronze-600/30 pl-4">
              {headings.map((h) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    className={
                      "block font-sans text-sm transition-colors " +
                      (active === h.id
                        ? "text-navy-800 font-semibold"
                        : "text-charcoal-500 hover:text-navy-800")
                    }
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </aside>

      {/* Body */}
      <article className="legal-article max-w-[720px]">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSlug]}
        >
          {body}
        </ReactMarkdown>
      </article>
    </div>
  );
}

export default MarkdownArticle;